import { useCallback, useRef, useState } from 'react';
import { useFormik, FormikErrors, FormikValues, FormikHelpers } from 'formik';
import axios from 'axios';
import { SPREEDLY_ENVIRONMENT_KEY } from '@ordermentum/ui-constants';
import { expiryDateModifier } from '../../../utilities';
import { paymentFormSchema } from '../../../utilities/formSchemas';
import useSpreedlySession from '../../../hooks/data/useSpreedlySession';
import useSpreedlySDK, {
  SpreedlyPaymentMethod,
} from '../../../hooks/useSpreedlySDK';
import { SecureCardPayload } from '../../../redux/payment_methods';

export type SecureCardFormValues = {
  cardName: string;
  expiryDate: string;
  cardType: string;
  defaultAll: boolean;
};

export type SecureCardFormErrors = SecureCardFormValues & {
  submit: string;
};

/**
 * Errors for Spreedly iframe fields.
 * These are separate from Formik errors since iframes aren't Formik-controlled.
 */
export type IframeFieldErrors = {
  cardNumber?: string;
  cvv?: string;
};

export type UseSecureCardFormProps = {
  params: { retailerId: string };
  purchaserId?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  addCardPayment: (cardDetails: SecureCardPayload, retailerId: string) => void;
  /** Callback fired when submitting state changes - called directly at submission start/end */
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export type UseSecureCardFormReturn = {
  // Form state
  values: SecureCardFormValues;
  errors: FormikErrors<SecureCardFormErrors>;
  iframeErrors: IframeFieldErrors;
  dirty: boolean;
  isSubmitting: boolean;

  // Form handlers
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  setFieldValue: (
    field: string,
    value: string | boolean,
    shouldValidate?: boolean
  ) => void;
  resetForm: () => void;
  handleCancel: () => void;
  handleExpiryDateChange: (field: string, value: string) => void;

  // Spreedly state
  isSDKReady: boolean;
  isInitialized: boolean;
  isSessionLoading: boolean;
  cardNumberElRef: React.RefObject<HTMLDivElement>;
  cvvElRef: React.RefObject<HTMLDivElement>;
};

/**
 * Splits a full name into first and last name
 * Handles edge cases like single names or multiple middle names
 */
export const splitFullName = (
  fullName: string
): { firstName: string; lastName: string } => {
  const trimmedName = fullName.trim();

  if (!trimmedName) {
    return { firstName: '', lastName: '' };
  }

  const nameParts = trimmedName.split(/\s+/);

  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: '' };
  }

  return {
    firstName: nameParts[0],
    lastName: nameParts.slice(1).join(' '),
  };
};

/**
 * Parses and validates expiry date from MM/YY format
 * Returns both month and full year (YYYY)
 *
 * @throws Error if month is not between 01-12 or year is not numeric
 */
export const parseExpiryDate = (
  expiryDate: string
): { month: string; year: string } => {
  const [month, yearShort] = expiryDate.split('/');

  if (!month || !yearShort) {
    throw new Error('Invalid expiry date format. Expected MM/YY');
  }

  // Validate month is numeric and between 01-12
  const monthNum = parseInt(month, 10);
  if (Number.isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    throw new Error('Invalid month. Must be between 01 and 12');
  }

  const yearNum = parseInt(yearShort, 10);
  if (Number.isNaN(yearNum)) {
    throw new Error('Invalid year format. Must be numeric');
  }

  // Convert YY to YYYY (assumes 20XX)
  const year = `20${yearShort}`;

  return { month, year };
};

/**
 * Normalizes card type from Spreedly to your system's issuer format
 */
export const normalizeCardType = (spreedlyCardType: string): string => {
  const cardTypeMap: Record<string, string> = {
    american_express: 'amex',
    visa: 'visa',
    mastercard: 'mastercard',
    discover: 'discover',
    diners_club: 'diners',
    jcb: 'jcb',
  };

  const normalized = cardTypeMap[spreedlyCardType.toLowerCase()];
  return normalized || spreedlyCardType.toLowerCase();
};

/**
 * Parses Spreedly tokenization errors into user-friendly messages
 */
export const getTokenizationErrorMessage = (error: unknown): string => {
  const defaultMessage =
    'Failed to process card information. Please try again.';

  if (!(error instanceof Error)) {
    return defaultMessage;
  }

  const errorString = error.message.toLowerCase();

  if (
    errorString.includes('card_type') ||
    errorString.includes('unable to determine the card type')
  ) {
    return 'Please enter a valid card number.';
  }

  if (errorString.includes('cvv')) {
    return 'Please enter a valid CVV.';
  }

  if (errorString.includes('expired')) {
    return 'This card has expired. Please use a different card.';
  }

  if (errorString.includes('timed out')) {
    return 'The request timed out. Please check your connection and try again.';
  }

  return defaultMessage;
};

/**
 * Extracts error message from API errors, prioritizing Axios response.data.message
 */
export const getApiErrorMessage = (
  error: unknown,
  defaultMessage = 'Failed to save card. Please try again.'
): string => {
  // Check for Axios error with response.data.message
  if (axios.isAxiosError(error) && error.response?.data?.message) {
    return error.response.data.message;
  }

  // Fallback to standard Error message
  if (error instanceof Error) {
    return error.message;
  }

  return defaultMessage;
};

// Validation schema adapted for Spreedly (card number and CVV handled by Spreedly)
// We omit cardNumber and cvv since they're handled by Spreedly iframes
const secureCardFormSchema = paymentFormSchema.omit(['cardNumber', 'cvv']);

export const useSecureCardForm = (
  props: UseSecureCardFormProps
): UseSecureCardFormReturn => {
  const {
    onSuccess = () => {},
    onClose,
    addCardPayment,
    params,
    purchaserId,
    onSubmittingChange,
  } = props;
  const { retailerId } = params;

  // Fetch Spreedly session data
  const { data: sessionData, isLoading: isSessionLoading } =
    useSpreedlySession();

  // Store payment method for submission
  const spreedlyPaymentMethodRef = useRef<SpreedlyPaymentMethod | null>(null);

  // Iframe field errors (separate from Formik since iframes aren't Formik-controlled)
  const [iframeErrors, setIframeErrors] = useState<IframeFieldErrors>({});

  // Initialize Spreedly SDK
  const {
    isSDKReady,
    isInitialized,
    fieldValidity,
    cardNumberElRef,
    cvvElRef,
    validate: validateSpreedly,
    tokenize,
  } = useSpreedlySDK({
    environmentKey: SPREEDLY_ENVIRONMENT_KEY,
    sessionData: sessionData ?? null,
    isLoading: isSessionLoading,
    onTokenizeSuccess: useCallback((paymentMethod: SpreedlyPaymentMethod) => {
      spreedlyPaymentMethodRef.current = paymentMethod;
    }, []),
  });

  const handleOnSubmit = useCallback(
    async (
      values: SecureCardFormValues,
      actions: FormikHelpers<FormikValues>
    ) => {
      try {
        // Clear previous iframe errors
        setIframeErrors({});

        // Notify parent that submission has started
        onSubmittingChange?.(true);

        // Check field validity from inputProperties before proceeding.
        // This gives us early feedback if fields are empty/invalid.
        if (!fieldValidity.validNumber || !fieldValidity.validCvv) {
          setIframeErrors({
            cardNumber: !fieldValidity.validNumber
              ? 'Please enter a valid card number.'
              : undefined,
            cvv: !fieldValidity.validCvv
              ? 'Please enter a valid CVV.'
              : undefined,
          });
          return;
        }

        // Note: This is a quick patch until we can find a better solution
        //
        // We currently need to do a manual blur of the iframe fields
        // here to ensure that spreedly is able to register and commit the values
        // on their end so that tokenization can pick up those values
        cardNumberElRef.current?.blur();
        cvvElRef.current?.blur();

        // Validate via Spreedly and wait for result
        const validationErrors = await validateSpreedly();
        if (validationErrors) {
          // Ideally we should be doing some tracking or analytics here
          // but since spreedly imposes that we don't add analytics to pages
          // where secure iframes exist, we will rely on console errors to help
          // support troubleshoot issues.
          console.error(
            '[SecureCardForm] Spreedly validation errors',
            validationErrors
          );
          // Map Spreedly validation errors to iframe field errors
          if (validationErrors.number || validationErrors.cvv) {
            setIframeErrors({
              cardNumber: validationErrors.number?.join(', '),
              cvv: validationErrors.cvv?.join(', '),
            });
            return;
          }
          // Handle any other validation errors via submit banner
          const errorMessages = Object.values(validationErrors).flat();
          if (errorMessages.length > 0) {
            actions.setFieldError('submit', errorMessages.join(', '));
            return;
          }
        }

        // Parse name
        const { firstName, lastName } = splitFullName(values.cardName);
        if (!firstName) {
          actions.setFieldError('cardName', 'Please enter a valid name');
          return;
        }

        let expiryMonth: string;
        let expiryYear: string;
        try {
          const parsed = parseExpiryDate(values.expiryDate);
          expiryMonth = parsed.month;
          expiryYear = parsed.year;
        } catch (error) {
          actions.setFieldError('expiryDate', 'Invalid expiry date format');
          return;
        }

        try {
          await tokenize({
            first_name: firstName,
            last_name: lastName,
            month: expiryMonth,
            year: expiryYear,
          });
        } catch (tokenError) {
          // Ideally we should be doing some tracking or analytics here
          // but since spreedly imposes that we don't add analytics to pages
          // where secure iframes exist, we will rely on console errors to help
          // support troubleshoot issues.
          console.error('[SecureCardForm] Tokenization failed', tokenError);
          actions.setFieldError(
            'submit',
            getTokenizationErrorMessage(tokenError)
          );
          return;
        }

        const paymentMethod = spreedlyPaymentMethodRef.current;
        if (!paymentMethod?.token) {
          actions.setFieldError(
            'submit',
            'Payment tokenization failed. Please try again.'
          );
          return;
        }

        const payload: SecureCardPayload = {
          expiryMonth: Number(expiryMonth),
          expiryYear: Number(expiryYear),
          issuer: normalizeCardType(paymentMethod.card_type),
          name: values.cardName,
          defaultPayment: true,
          defaultAll: values.defaultAll,
          userId: retailerId,
          purchaserId,
          paymentMethodToken: paymentMethod.token,
          lastFourDigits: paymentMethod.last_four_digits,
        };

        try {
          await addCardPayment(payload, retailerId);
        } catch (apiError: unknown) {
          actions.setFieldError('submit', getApiErrorMessage(apiError));
          return;
        }

        actions.resetForm();
        onSuccess();
      } catch (ex) {
        actions.setFieldError(
          'submit',
          'An unexpected error occurred. Please try again.'
        );
      } finally {
        actions.setSubmitting(false);
        // Notify parent that submission has ended
        onSubmittingChange?.(false);
      }
    },
    [
      addCardPayment,
      fieldValidity,
      retailerId,
      onSuccess,
      tokenize,
      validateSpreedly,
      purchaserId,
      onSubmittingChange,
      cardNumberElRef,
      cvvElRef,
    ]
  );

  const {
    dirty,
    handleSubmit,
    values,
    errors,
    setFieldValue,
    isSubmitting,
    resetForm,
  } = useFormik<SecureCardFormValues>({
    validationSchema: secureCardFormSchema,
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
    initialValues: {
      cardName: '',
      expiryDate: '',
      cardType: '',
      defaultAll: false,
    },
    onSubmit: handleOnSubmit,
  });

  const handleCancel = useCallback(() => {
    resetForm();
    setIframeErrors({});
    if (onClose) {
      onClose();
    }
  }, [resetForm, onClose]);

  const handleExpiryDateChange = useCallback(
    (field: string, value: string) => {
      setFieldValue(field, expiryDateModifier(value));
    },
    [setFieldValue]
  );

  return {
    // Form state
    values,
    errors,
    iframeErrors,
    dirty,
    isSubmitting,

    // Form handlers
    handleSubmit,
    setFieldValue,
    resetForm,
    handleCancel,
    handleExpiryDateChange,

    // Spreedly state
    isSDKReady,
    isInitialized,
    isSessionLoading,
    cardNumberElRef,
    cvvElRef,
  };
};

export default useSecureCardForm;
